// A new endpoint: the same form and preview as editing, then "Create",
// which saves v1 as a draft (nothing is published).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'

import { api, can, type EndpointReport } from '../api'
import { ErrorBox } from '../components'
import { SpecEditor, SpecReportView } from './EndpointDetail'
import { blankSpec, normalizeSpec, type Spec } from '../endpointSpec'
import { EndpointPreview } from './EndpointForm'

const slugFromPath = (path: string) =>
  path
    .replace(/^\/v[0-9]+\//, '')
    .replace(/\{[^}]+\}/g, '')
    .split('/')
    .filter(Boolean)
    .join('-')
    .replace(/[^a-z0-9-]/g, '')
    .slice(0, 80)

export function EndpointNewPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const [spec, setSpec] = useState<Spec>(blankSpec)
  const [slug, setSlug] = useState('')
  const [comment, setComment] = useState('')
  const [report, setReport] = useState<EndpointReport | null>(null)
  const effSlug = slug || slugFromPath(spec.path ?? '')
  const validate = useMutation({ mutationFn: () => api.validateEndpoint(effSlug || 'new', normalizeSpec(spec)), onSuccess: setReport })
  const create = useMutation({
    mutationFn: () => api.createEndpoint(effSlug, normalizeSpec(spec), comment || 'created'),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ['endpoints'] })
      navigate(`/endpoints/${d.slug}`)
    },
  })
  if (me.data && !can(me.data, 'editor')) return <p className="callout err">Creating endpoints needs the editor role.</p>
  return (
    <section className="page">
      <p className="crumbs">
        <Link to="/endpoints">Endpoints</Link> / new
      </p>
      <header className="page-h">
        <div>
          <h1>New endpoint</h1>
          <p className="sub">Pick a bucket, choose what callers get and how they can filter it, and try it below. Creating saves v1 as a draft.</p>
        </div>
      </header>
      <div className="card">
        <label>
          Slug (the endpoint's id)
          <input className="mono" value={slug} placeholder={slugFromPath(spec.path ?? '') || 'weather-now'} onChange={(e) => setSlug(e.target.value)} />
        </label>
        <SpecEditor spec={spec} onChange={(s) => {
          setSpec(s)
          setReport(null)
        }} readOnly={false} />
        <div className="row">
          <button className="btn" disabled={validate.isPending || !spec.bucket} onClick={() => validate.mutate()}>
            Validate
          </button>
          <input className="grow" placeholder="Comment (optional)" value={comment} onChange={(e) => setComment(e.target.value)} />
          <button className="btn btn-primary" disabled={create.isPending || !spec.bucket || !effSlug} onClick={() => create.mutate()}>
            Create {effSlug && <span className="mono">{effSlug}</span>}
          </button>
        </div>
        {validate.error && <ErrorBox error={validate.error} />}
        {create.error && <ErrorBox error={create.error} />}
        {report && <SpecReportView report={report} />}
      </div>
      <EndpointPreview spec={spec} />
    </section>
  )
}
